import React, { useEffect, useMemo, useRef } from 'react';
import { batch, useDispatch, useSelector } from 'react-redux';
import styled from '@emotion/styled';
import PropTypes from 'prop-types';

import Checkbox from '@material-ui/core/Checkbox';
import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';

import Button from '../../../../shared/_TBComponents/Button';
import Dropdown from '../../../force-plate-dashboard/components/presentational/ForcePlateDropDown';
import Text from '../../../../shared/_TBComponents/Text';
import TBTooltip from '../../../../shared/components/TBTooltip/TBTooltip';
import { BoxShadow } from '../../../../shared/GlobalStyles';
import {
  setIsExerciseFiltersShowing,
  setExerciseFilters,
  setBucketSize,
  setOverviewRangeMax,
  setOverviewRangeMin,
  setAssignedCompletedRangeMax,
  setAssignedCompletedRangeMin,
} from '../../ducks/volumeTrackerDashboardActions';
import { dashboardTracker } from '../../../../shared/utils/amplitudeHelper';

const FilterAndButtonContainer = styled('div')`
  position: relative;
  display: flex;
  align-items: center;
`;

const Badge = styled('span')`
  position: absolute;
  top: -7px;
  right: -7px;
  border-radius: 100px;
  background-color: #444444;
  color: #FFFFFF;
  width: 16px;
  display: flex;
  justify-content: center;
  font-family: 'Nunito Sans';
  font-size: 11px;
  font-weight: bold;
`;

const FilterContainer = styled('div')`
  display: flex;
  position: absolute;
  top: 50px;
  background-color: #FFFFFF;
  z-index: 2;
  flex-direction: column;
  width: 275px;
  max-height: 420px;
  border-radius: 4px;
  padding: 16px;
  box-shadow: ${BoxShadow};
  overflow-y: auto;
`;

const FilterHeader = styled('div')`
  display: flex;
  justify-content: space-between;
  font-family: 'Nunito Sans';
  font-size: 20px;
  font-weight: 700;
  margin-bottom: 24px;
`;

const FilterSubHeader = styled('div')`
  font-family: 'Nunito Sans';
  font-size: 16px;
  font-weight: 700;
`;

const Filters = styled('div')`
  font-family: 'Nunito Sans';
`;

const DropdownContainer = styled('div')`
  display: flex;
  flex-direction: column;
  margin-top: ${(props) => (props.vtDashView === 'assigned' ? '12px' : '4px')};
`;

const DropdownOption = styled('div')`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const FilterOption = styled('div')`
  display: flex;
  align-items: center;
  margin-left: -10px;

  h3 {
    font-weight: normal;
  }
`;

const RadioGroupContainer = styled('div')`
  display: flex;
  margin: 4px 0px 24px -6px;
`;

const RadioOptionContainer = styled('div')`
  display: flex;
  align-items: center;
`;

const OptionText = styled('div')`
  font-family: 'Nunito Sans';
  font-weight: normal;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
  color: ${(props) => (props.isDisabled ? 'rgba(0, 0, 0, 0.26)' : '#444444')};
`;

const FilterBottom = styled('div')`
  display: flex;
  justify-content: space-around;
  margin-top: 16px;
  gap: 18px;
`;

const ExerciseFilter = ({ viewOnly = false }) => {
  const dispatch = useDispatch();

  const assignedCompletedRangeMax = useSelector(
    (state) => state.volumeTrackerDashboard.data.assignedCompletedRangeMax,
  );
  const assignedCompletedRangeMin = useSelector(
    (state) => state.volumeTrackerDashboard.data.assignedCompletedRangeMin,
  );
  const bucketSize = useSelector(
    (state) => state.volumeTrackerDashboard.data.bucketSize,
  );
  const exerciseFilters = useSelector(
    (state) => state.volumeTrackerDashboard.data.exerciseFilters,
  );
  const exercises = useSelector(
    (state) => state.volumeTrackerDashboard.data.exercises,
  );
  const overviewRangeMax = useSelector(
    (state) => state.volumeTrackerDashboard.data.overviewRangeMax,
  );
  const overviewRangeMin = useSelector(
    (state) => state.volumeTrackerDashboard.data.overviewRangeMin,
  );

  const isExerciseFiltersShowing = useSelector(
    (state) => state.volumeTrackerDashboard.ui.isExerciseFiltersShowing,
  );
  const vtDashView = useSelector(
    (state) => state.volumeTrackerDashboard.ui.vtDashView,
  );

  const bucketSizeRef = useRef(bucketSize);

  const isAssignedView = vtDashView === 'assigned';
  const vtDashViewLabel = isAssignedView ? 'Assigned/Completed' : 'Overview';

  const rangeMin = isAssignedView ? assignedCompletedRangeMin : overviewRangeMin;
  const rangeMax = isAssignedView ? assignedCompletedRangeMax : overviewRangeMax;
  const setRangeMin = isAssignedView ? setAssignedCompletedRangeMin : setOverviewRangeMin;
  const setRangeMax = isAssignedView ? setAssignedCompletedRangeMax : setOverviewRangeMax;

  const ranges = {
    5: { overview: 50, assigned: 25 },
    10: { overview: 100, assigned: 50 },
  };
  const range = ranges[bucketSize]?.[vtDashView] ?? 50;
  const hundredPlusId = bucketSize === 5 ? 105 : 110;

  // Adjust min/max to help fit selected bucket size
  const normalizeRange = (min, max) => {
    const roundedMax = max % bucketSize !== 0 ? Math.ceil(max / bucketSize) * bucketSize : max;
    let roundedMin = min % bucketSize !== 0 ? Math.floor(min / bucketSize) * bucketSize : min;

    const maxRange = ranges[bucketSize]?.[vtDashView] ?? bucketSize * 5;
    if (roundedMax - roundedMin > maxRange) {
      roundedMin = Math.max(0, roundedMax - maxRange);
    }

    return [roundedMin, roundedMax];
  };

  // Check if min/max are valid for bucket size/available dropdown options
  const isRangeValid = (min, max) => {
    const isHundredPlus = max === hundredPlusId;
    if (isHundredPlus) {
      return (hundredPlusId - min) <= range;
    }
    return max - min <= range;
  };

  const hasFilters = exerciseFilters.length > 0;
  const sortedExercises = [...exercises].sort((a, b) => a.name.localeCompare(b.name));

  const dropdownOptions = useMemo(() => {
    const steps = Math.floor(100 / bucketSize) + 1;

    const baseOptions = Array.from({ length: steps }, (_, i) => ({
      id: i * bucketSize,
      label: `${i * bucketSize}%`,
    }));
    // Add 100+ as extra catch all option
    return [...baseOptions, { id: hundredPlusId, label: '100%+' }];
  }, [bucketSize]);

  const fromOptions = useMemo(() => {
    const maxMinValue = bucketSize === 5 ? 100 : 100;
    const steps = Math.floor(maxMinValue / bucketSize) + 1;

    return Array.from({ length: steps }, (_, i) => {
      const value = i * bucketSize;
      return {
        id: value,
        label: `${value}%`,
      };
    });
  }, [bucketSize]);

  const getRangeOptions = (fromValueId) => {
    if (fromValueId == null) return [];

    if (fromValueId === 100) {
      return [{ id: hundredPlusId, label: '100%+' }];
    }

    const min = fromValueId + bucketSize;
    const max = fromValueId + range;
    const rangeArray = Array.from({ length: (max - min) / bucketSize + 1 }, (_, i) => {
      const value = min + i * bucketSize;
      return { id: value, label: `${value}%` };
    }).filter(({ id }) => id <= 100);

    // Find new min value for 100+ option
    const minValueForHundredPlus = vtDashView === 'overview' ? 10 : 60;
    const allowHundredPlus = fromValueId >= minValueForHundredPlus
    && (hundredPlusId - fromValueId) <= range;

    if (allowHundredPlus) {
      rangeArray.push({ id: hundredPlusId, label: '100%+' });
    }

    return rangeArray;
  };

  const getDefaultMaxOption = () => dropdownOptions.find((opt) => opt.id === rangeMax);

  const handleIncrementChange = (e) => {
    if (viewOnly) return;
    dispatch(setBucketSize(parseInt(e.target.value, 10)));
  };

  useEffect(() => {
    const prev = bucketSizeRef.current;
    if (prev === bucketSize) return;
    if (viewOnly) return;

    bucketSizeRef.current = bucketSize;
    const prevHundredPlusId = prev === 5 ? 105 : 110;
    const newHundredPlusId = bucketSize === 5 ? 105 : 110;

    const [newMin, newMax] = normalizeRange(rangeMin, rangeMax);
    const isHundredPlus = rangeMax === prevHundredPlusId;
    const fallbackMaxOption = getRangeOptions(newMin).find((opt) => opt.id === newHundredPlusId);

    // Switch to 100%+ if selected max exceeds 100 and is not valid bucket option
    if (isHundredPlus) {
      batch(() => {
        dispatch(setRangeMin(newMin));
        dispatch(setRangeMax(fallbackMaxOption?.id ?? newHundredPlusId));
      });
      return;
    }
    // Keep range if valid after rounding
    if (isRangeValid(newMin, newMax)) {
      if (newMin !== rangeMin) dispatch(setRangeMin(newMin));
      if (newMax !== rangeMax) dispatch(setRangeMax(newMax));
      return;
    }

    const fallback = getRangeOptions(newMin)[0];
    batch(() => {
      dispatch(setRangeMin(newMin));
      dispatch(setRangeMax(fallback?.id || null));
    });
  }, [bucketSize, rangeMin, rangeMax, dispatch]);

  useEffect(() => {
    if (vtDashView !== 'assigned') return;
    if (viewOnly) return;

    const bucketRange = rangeMax - rangeMin;
    const maxBucketRange = bucketSize * 5;

    if (bucketRange > maxBucketRange) {
      const fallbackMin = Math.max(0, rangeMax - maxBucketRange);
      dispatch(setRangeMin(fallbackMin));
    }
  }, [vtDashView, rangeMin, rangeMax, bucketSize, dispatch]);

  const handleRangeMin = (selected) => {
    if (viewOnly) return;
    const newMin = selected.id;
    dispatch(setRangeMin(newMin));

    const validMaxOptions = getRangeOptions(newMin);
    const validMaxIds = validMaxOptions.map((opt) => opt.id);

    if (!validMaxIds.includes(rangeMax)) {
      const fallback = validMaxOptions[0];
      dispatch(setRangeMax(fallback?.id || null));
    }
  };

  const handleFilter = () => {
    // Amplitude tracking
    dashboardTracker('Volume Tracker', `${vtDashViewLabel} - Filter`, 'Filter clicked');
    dispatch(setIsExerciseFiltersShowing(!isExerciseFiltersShowing));
  };

  const handleClose = () => {
    // Amplitude tracking
    dashboardTracker('Volume Tracker', `${vtDashViewLabel} - Filter`, 'Filter closed');
    dispatch(setIsExerciseFiltersShowing(false));
  };

  const handleChange = (exercise) => {
    // Amplitude tracking
    dashboardTracker(
      'Volume Tracker',
      `${vtDashViewLabel} - Filter`,
      `Exercise filter clicked - ${exercise.name}`,
    );
    // If exercise is already in filters then remove it
    if (exerciseFilters.includes(exercise)) {
      const newExercises = [...exerciseFilters];
      const index = newExercises.indexOf(exercise);
      if (index > -1) {
        newExercises.splice(index, 1);
      }
      dispatch(setExerciseFilters(newExercises));
    // Else add it to the filters array
    } else {
      const newExercises = [...exerciseFilters];
      newExercises.push(exercise);
      dispatch(setExerciseFilters(newExercises));
    }
  };

  const handleClear = () => {
    // Amplitude tracking
    dashboardTracker('Volume Tracker', `${vtDashViewLabel} - Filter`, 'Filters cleared');
    batch(() => {
      dispatch(setExerciseFilters([]));
      if (!viewOnly) {
        dispatch(setBucketSize(10));
        dispatch(setRangeMin(50));
        dispatch(setRangeMax(100));
      }
    });
  };

  const getActiveFilterCount = () => exerciseFilters.length;

  return (
    <FilterAndButtonContainer>
      <Button
        icon='filter'
        name='Filter'
        upperCase
        rounded
        styledWrapperProps={{
          padding: '11px 18px',
          border: '1px solid #D8D8D8',
          backgroundColor: '#FFFFFF',
          filter: 'none',
          boxShadow: 'none',
          transition: 'border 100ms ease-in-out',
          '&:hover': {
            border: '1px solid #B0B0B0',
            backgroundColor: '#FFFFFF',
            filter: 'none',
            boxShadow: 'none',
          },
        }}
        onClick={handleFilter}
      />
      {getActiveFilterCount() > 0 && (
        <Badge>{getActiveFilterCount()}</Badge>
      )}
      {isExerciseFiltersShowing && (
        <FilterContainer>
          <FilterHeader>
            Filter
            <Button
              icon='remove'
              iconColor='#9E9E9E'
              iconOnly
              noBorder
              onClick={handleClose}
            />
          </FilterHeader>
          <Filters>
            <FilterSubHeader>
              Percentage Buckets
            </FilterSubHeader>
            <DropdownContainer vtDashView={vtDashView}>
              {isAssignedView && (
                <Text styledTextProps={{ fontSize: '14px' }}>
                  Assigned/Completed Percentages
                </Text>
              )}
              <DropdownOption>
                <Dropdown
                  list={fromOptions}
                  label='label'
                  placeholder=''
                  selectItemFunction={handleRangeMin}
                  defaultOption={
                    fromOptions.find((opt) => opt.id === rangeMin) || {}
                  }
                  disabled={viewOnly}
                  readOnly
                />
                <Text
                  styledTextProps={{ fontSize: '14px' }}
                  styledWrapperProps={{ margin: '0px 6px 0px -4px' }}
                >
                  to
                </Text>
                <Dropdown
                  list={getRangeOptions(rangeMin)}
                  label='label'
                  placeholder=''
                  selectItemFunction={(item) => {
                    dispatch(setRangeMax(item.id));
                  }}
                  defaultOption={getDefaultMaxOption()}
                  disabled={viewOnly}
                  readOnly
                />
              </DropdownOption>
            </DropdownContainer>
          </Filters>
          <Filters>
            <RadioGroupContainer>
              <RadioGroup
                row
                value={bucketSize.toString()}
                onChange={handleIncrementChange}
              >
                <RadioOptionContainer>
                  <Radio value='10' size='small' color='default' disabled={viewOnly} />
                  <Text styledTextProps={{ fontSize: '14px' }}>
                    10% Increments
                  </Text>
                </RadioOptionContainer>
                <RadioOptionContainer>
                  <Radio value='5' size='small' color='default' disabled={viewOnly} />
                  <Text styledTextProps={{ fontSize: '14px' }}>
                    5% Increments
                  </Text>
                </RadioOptionContainer>
              </RadioGroup>
            </RadioGroupContainer>
          </Filters>
          <Filters>
            <FilterSubHeader>
              Exercises Selected
            </FilterSubHeader>
            {sortedExercises.map((option) => (
              <FilterOption key={option.id}>
                <Checkbox
                  color='default'
                  checked={exerciseFilters.some((ex) => ex.id === option.id)}
                  disabled={
                    hasFilters && exerciseFilters[0].unitAbbr !== option.unitAbbr
                  }
                  onChange={() => handleChange(option)}
                />
                {option.name.length > 20 ? (
                  <TBTooltip
                    textToDisplay={option.name}
                    textToHover={option.name}
                    fontSize='16px'
                    textContainerPadding='0px'
                    textContainerMaxWidth='170px'
                    fontColor={
                      hasFilters && exerciseFilters[0].unitAbbr !== option.unitAbbr
                        ? 'rgba(0, 0, 0, 0.26)' : '#444444'
                    }
                  />
                ) : (
                  <OptionText
                    isDisabled={
                      hasFilters && exerciseFilters[0].unitAbbr !== option.unitAbbr
                    }
                  >
                    {option.name}
                  </OptionText>
                )}
              </FilterOption>
            ))}
          </Filters>
          <FilterBottom>
            <Button
              name='Clear'
              upperCase
              rounded
              fullWidth
              onClick={handleClear}
            />
            <Button
              name='Done'
              upperCase
              bgColor='#444444'
              txtColor='#FFFFFF'
              rounded
              fullWidth
              disabled={exerciseFilters.length === 0}
              onClick={handleClose}
            />
          </FilterBottom>
        </FilterContainer>
      )}
    </FilterAndButtonContainer>
  );
};

ExerciseFilter.propTypes = {
  viewOnly: PropTypes.bool,
};

ExerciseFilter.defaultProps = {
  viewOnly: false,
};

export default ExerciseFilter;
